/** @format */

import { axiosInstance } from '@common/application/context/Providers';
import { Resource } from '@common/application/enums/Resource';
import { ResourceUrl } from '@common/application/helpers/generateSignedUrlsForResults';
import uploadFileToSignedUrl from '@common/application/helpers/uploadFileToSignedUrl';
import { Account } from '@common/model/apiClient/Account';
import { Document } from '@common/model/apiClient/Document';
import { User } from '@common/model/apiClient/User';
import { AxiosApiResponse } from '@common/types/api';
import { ElasticQueryOpts, ElasticResponse, ElasticSort } from '@common/types/elastic';
import { Nullable } from '@common/types/utils';
import { Branch } from './Branch';

export class Company {
  id: number;
  logo: ResourceUrl;
  name: string;
  realm: string;
  createdAt;
  updatedAt;
  companyDocumentStyle;
  deletedAt;

  constructor(name, realm, logo) {
    this.name = name;
    this.realm = realm;
    this.logo = logo;
  }

  static async list(
    query: string,
    sort: ElasticSort,
    queryOpts: ElasticQueryOpts,
    size?: number,
    pit?: Nullable<string>,
    search_after?: Nullable<ElasticSort>,
  ): ElasticResponse<Company> {
    // Ensure this code is not ran by SSR!
    if (typeof window !== 'undefined') {
      const res = await axiosInstance.get(`/api/v1/companies`, {
        params: {
          sequence_id: 1,
          pit: pit,
          query: query,
          queryOpts: JSON.stringify(queryOpts ?? { deleted: false }),
          sort: JSON.stringify(sort),
          search_after: JSON.stringify(search_after),
          size: size,
        },
      });
      return { ...res.data };
    }
    throw new Error();
  }

  static autocomplete(query, config) {
    if (typeof window !== 'undefined') {
      return axiosInstance
        .get(`/api/v1/companies/search`, {
          signal: config.signal,
          params: {
            sequence_id: 1,
            query,
          },
        })
        .then((res) => ({ ...res.data }));
    }
    throw new Error();
  }

  static getById(companyId): AxiosApiResponse<Company> {
    return axiosInstance.get(`/api/v1/companies/${companyId}`);
  }

  static get(): AxiosApiResponse<Company> {
    return axiosInstance.get(`/api/v1/companies/this`);
  }

  static delete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.delete(`/api/v1/companies/${id}`).then((res) => ({ ...res.data }));
  }

  static undelete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.post(`/api/v1/companies/${id}/undelete`).then((res) => ({ ...res.data }));
  }

  static getDocTotals(timeSpan: { start: Date; end: Date }): Promise<{ message: string; paginated: boolean; payload: { totalCompanySavings: string; documentCount: string } }> {
    return axiosInstance.get(`/api/v1/companies/totals`, { params: { start: timeSpan.start, end: timeSpan.end } }).then((res) => ({ ...res.data }));
  }

  static getDashboardStats(): Promise<{
    message: string;
    paginated: boolean;
    payload: { unapprovedContactCount: number; unapprovedIndustryCount: number; missingAccountLogoCount: number; recentDocuments: Array<Document> };
  }> {
    return axiosInstance.get(`/api/v1/companies/dashboard`).then((res) => ({ ...res.data }));
  }

  static getRankings(timeSpan: { startDate: Date; endDate: Date }): Promise<{
    message: string;
    paginated: boolean;
    payload: {
      topAccounts: Array<Account>;
      topBranches: Array<Branch>;
      topUsers: Array<{ total_savings: { value: number }; author: { hits: Array<{ _source: User }> } }>;
    };
  }> {
    return axiosInstance.get(`/api/v1/companies/rankings`, { params: { start: timeSpan.startDate, end: timeSpan.endDate } }).then((res) => ({ ...res.data }));
  }

  static getRecentDocuments(): Promise<{
    message: string;
    paginated: boolean;
    payload: {
      recentDocuments: Array<{ _source: Document; _id: string }>;
    };
  }> {
    return axiosInstance.get(`/api/v1/companies/recentdocuments`).then((res) => ({ ...res.data }));
  }

  static getAddTemplate(): Promise<Record<string, unknown>> {
    return axiosInstance.get(`/api/v1/templates/companies/add`, {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  add(fileName: string, fileExt: string): Promise<Record<string, unknown>> {
    return axiosInstance
      .post(`/api/v1/companies`, {
        payload: {
          name: this.name,
          realm: this.realm,
          logo: { dataPresent: !!this.logo, ext: fileExt, name: fileName },
        },
      })
      .then(async (res) => {
        const { signedUrl } = res.data.payload;
        await uploadFileToSignedUrl(signedUrl, this.logo.url as File, res.data.payload, 'logo', Resource.COMPANY_LOGO);
        return { ...res.data };
      });
  }

  static update(updatedCompany: { name: string; logo: Nullable<File>; companyDocumentStyle?: Record<string, any> }, id: number): Promise<Record<string, unknown>> {
    return axiosInstance
      .patch(`/api/v1/companies/${id}`, {
        payload: { ...updatedCompany, logo: updatedCompany.logo ? { dataPresent: true, name: updatedCompany.logo.name } : null },
      })
      .then(async (res) => {
        const { signedUrl } = res.data.payload;
        if (signedUrl) {
          await uploadFileToSignedUrl(signedUrl, updatedCompany.logo as File, res.data.payload, 'logo', Resource.COMPANY_LOGO);
        }
        return { ...res.data };
      });
  }
}
